import { useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { useNavigate } from "react-router-dom";
import {
  LuLock,
  LuEye,
  LuEyeOff,
  LuMail,
} from "react-icons/lu";

export default function Login({ onLogin }) {
  const [loader, setLoader] = useState(false);
  const [adminId, setAdminId] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoader(true);

    const body = {
      a_email: adminId,
      a_password: password,
    };

    try {
      const resp = await Calling.AdminLogin(body);
      const { status, token, message } = resp;

      if (status) {
        toast.success(message || "Login Successful!", { autoClose: 2000 });
        localStorage.setItem("admin_box_dollar", token);
        onLogin(true);
        navigate("/");
      } else {
        toast.error(message || "Invalid credentials.", { autoClose: 2000 });
        setLoader(false);
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!", { autoClose: 2000 });
      setLoader(false);
    }
  };

  return (
    <>
      {loader && <Loader />}

      <div className="min-h-screen w-full flex items-center justify-center bg-main-bg p-4">
        <div className="ui-card-solid w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 rounded-3xl overflow-hidden">
          {/* ===== Left brand panel ===== */}
          <div className="relative hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 text-white overflow-hidden">
            {/* Decorative shapes */}
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-20 -left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute top-1/3 left-1/2 w-40 h-40 bg-white/5 rounded-full blur-xl" />

            <div className="relative flex items-center gap-3">
              <div className="bg-white/15 backdrop-blur p-2 rounded-2xl shadow-lg shadow-black/20">
                <img
                  src={`${process.env.PUBLIC_URL}/rewardedplay.png`}
                  alt="Rewarded Play"
                  className="w-9 h-9 rounded-xl object-contain"
                />
              </div>
              <span className="font-bold text-lg tracking-wide">Rewarded <span className="text-gold-500">Play</span></span>
            </div>

            <div className="relative">
              <h2 className="text-4xl font-extrabold leading-tight mb-4">
                Welcome back, <br /> Administrator.
              </h2>
              <p className="text-white/80 text-sm leading-relaxed max-w-sm">
                Manage users, tasks, ads, and withdrawals from one secure,
                powerful control center.
              </p>
            </div>

            <div className="relative flex items-center gap-2 text-xs text-white/70">
              <LuLock className="text-white/60" />
              Encrypted &amp; secure sign in
            </div>
          </div>

          {/* ===== Right form panel ===== */}
          <div className="flex flex-col justify-center p-8 sm:p-12">
            <div className="mb-8 text-center lg:text-left">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-white rounded-2xl shadow-lg shadow-teal-500/20 ring-1 ring-brand-teal/20 mb-4 overflow-hidden">
                <img
                  src={`${process.env.PUBLIC_URL}/rewardedplay.png`}
                  alt="Rewarded Play"
                  className="w-full h-full object-contain p-1"
                />
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-main-font tracking-tight">
                Rewarded <span className="text-gradient-teal">Play</span>
              </h1>
              <p className="text-sm text-main-subfont mt-2">
                Admin Dashboard — please sign in to continue
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-main-font mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <LuMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-main-subfont text-lg z-10" />
                  <input
                    type="email"
                    value={adminId}
                    onChange={(e) => setAdminId(e.target.value)}
                    className="ui-input w-full pl-11 pr-4 py-3 rounded-xl text-sm md:text-base text-main-font placeholder-main-subfont/70 focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30 outline-none transition"
                    placeholder="Enter admin email"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-main-font mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <LuLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-main-subfont z-10" />
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="ui-input w-full pl-11 pr-11 py-3 rounded-xl text-sm md:text-base text-main-font placeholder-main-subfont/70 focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30 outline-none transition"
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-main-subfont hover:text-brand-teal transition"
                    tabIndex={-1}
                  >
                    {showPw ? <LuEyeOff /> : <LuEye />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loader}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white py-3.5 rounded-xl font-semibold text-base shadow-lg shadow-teal-500/30 hover:shadow-xl hover:brightness-110 active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed transition"
              >
                {loader ? "Signing in..." : "Login"}
              </button>
            </form>

            <p className="text-xs text-main-subfont text-center mt-8">
              © {new Date().getFullYear()} Admin Control Panel. All rights
              reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
